import React, { useState, useRef } from 'react';
import { useStore } from '../../store';
import { ENABLE_ACTIONS, GIS } from '../../actions/map.actions';
import { SET_USER } from '../../actions/rent.actions';
import { useToasts } from 'react-toast-notifications';
import SuccessIcon from '../../assets/success.png';
import Modal from 'react-modal';
import { Circle as CircleGeometry } from 'ol/geom';
import { GeoJSON } from 'ol/format';
import ReCAPTCHA from 'react-google-recaptcha';
import { Loader2, Mail, ShieldCheck } from 'lucide-react';
import { SuccessBridgeModal } from '../ui/SuccessBridgeModal';
import { trackEvent, trackFunnelStep, FunnelSteps } from '../../lib/analytics';
import {
  formatPhoneDisplay,
  sanitizePhoneInput,
  validateArgentinaPhone,
} from '../../lib/argentinaPhone';
import { getApiBaseUrl } from '../../lib/api';
import { clearFirstTouchUtm, getFirstTouchUtm } from '../../lib/acquisition';
import { siteConfig } from '../../config/site';
import {
  getFeatureHectares,
  getFieldAreaError,
  isFieldAreaValid,
} from '../../lib/fieldArea';
import { cn } from '../../lib/cn';

Modal.setAppElement('#root');

const soloLetras = (texto) => texto.replace(/[^a-zA-ZÁÉÍÓÚáéíóúÑñ\s]/g, '');

const errorModalStyles = {
  content: {
    top: '50%', left: '50%', right: 'auto', bottom: 'auto',
    marginRight: '-50%', transform: 'translate(-50%, -50%)',
    borderRadius: '16px', padding: '2rem', maxWidth: '90%', width: '420px', border: 'none',
  },
  overlay: { backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9998 },
};

const Form = ({ onStepChange }) => {
  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [mail, setMail] = useState('');
  const [telefono, setTelefono] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [{ map }, dispatch] = useStore();
  const { addToast } = useToasts();
  const [captchaValido, setCaptchaValido] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const recaptchaRef = useRef(null);
  const firstTouchUtmRef = useRef(null);
  if (firstTouchUtmRef.current === null) {
    firstTouchUtmRef.current = getFirstTouchUtm();
  }
  const fieldHectares = map?.gis ? getFeatureHectares(map.gis) : null;
  const hasValidField = isFieldAreaValid(fieldHectares);
  const hasPolygon = map?.hasPolygon;
  const fieldAreaError = getFieldAreaError(fieldHectares);
  const phoneValidation = validateArgentinaPhone(telefono);
  const showPhoneError = phoneTouched && telefono.trim() && !phoneValidation.valid;

  const onCaptchaChange = (value) => {
    setCaptchaValido(!!value);
    setRecaptchaToken(value || '');
  };

  const handleFieldFocus = () => {
    trackFunnelStep(FunnelSteps.EMAIL_STARTED);
    if (onStepChange) onStepChange(2);
  };

  const resetForm = () => {
    setNombre('');
    setApellido('');
    setMail('');
    setTelefono('');
    setPhoneTouched(false);
    setCaptchaValido(false);
    setRecaptchaToken('');
    if (recaptchaRef.current) recaptchaRef.current.reset();
  };

  const handleSuccessClose = () => {
    setSuccessOpen(false);
    resetForm();
    trackFunnelStep(FunnelSteps.ONBOARDING_COMPLETED);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => window.location.reload(), 500);
  };

  const evaluate = () => {
    if (!hasPolygon) {
      addToast('Primero marcá tu lote en el mapa.', { appearance: 'warning', autoDismiss: true });
      document.getElementById('rent-map-container')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (!hasValidField) {
      const hectares = fieldHectares ?? getFeatureHectares(map.gis);
      const message =
        getFieldAreaError(hectares) ||
        `El lote debe estar entre ${siteConfig.minHa} y ${siteConfig.freeHa} ha. Ajustá el polígono en el mapa.`;
      addToast(message, { appearance: 'warning', autoDismiss: true });
      document.getElementById('rent-map-container')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (!captchaValido) {
      addToast('Validá que no sos un robot.', { appearance: 'warning', autoDismiss: true });
      return;
    }

    if (!nombre.trim() || !apellido.trim()) {
      addToast('Ingresá nombre y apellido.', { appearance: 'warning', autoDismiss: true });
      return;
    }

    if (!mail.trim()) {
      addToast('Ingresá tu email.', { appearance: 'warning', autoDismiss: true });
      return;
    }

    const mailRegex = /^[\w.+-]+@[\w.-]+\.\w{2,9}$/;
    if (!mailRegex.test(mail)) {
      addToast('Ingresá un email válido.', { appearance: 'warning', autoDismiss: true });
      return;
    }

    const phoneCheck = validateArgentinaPhone(telefono);
    if (!phoneCheck.valid) {
      setPhoneTouched(true);
      addToast(phoneCheck.message, { appearance: 'warning', autoDismiss: true });
      return;
    }

    dispatch({
      type: SET_USER,
      payload: { nombre, apellido, mail, telefono: phoneCheck.e164 || '' },
    });
    trackFunnelStep(FunnelSteps.EMAIL_SUBMITTED);
    trackFunnelStep(FunnelSteps.REPORT_REQUESTED);
    if (phoneCheck.e164) {
      trackFunnelStep(FunnelSteps.PHONE_PROVIDED);
    }

    const feature = map.gis;
    const submitHectares = getFeatureHectares(feature);
    if (!isFieldAreaValid(submitHectares)) {
      addToast(
        getFieldAreaError(submitHectares) ||
          `El lote debe estar entre ${siteConfig.minHa} y ${siteConfig.freeHa} ha.`,
        { appearance: 'warning', autoDismiss: true },
      );
      document.getElementById('rent-map-container')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const geometry = feature.getGeometry();
    const geoJsonFormat = new GeoJSON();
    let geoJsonGeometry = JSON.parse(
      geoJsonFormat.writeGeometry(geometry, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857',
      }),
    );

    if (geometry instanceof CircleGeometry) {
      geoJsonGeometry.type = 'circle';
      geoJsonGeometry.radius = geometry.getRadius();
    }

    setIsSubmitting(true);
    if (onStepChange) onStepChange(3);

    fetch(`${getApiBaseUrl()}/api/v1/landing/`, {
      method: 'POST',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify({
        first_name: nombre,
        last_name: apellido,
        email: mail,
        ...(phoneCheck.e164 ? { phone: phoneCheck.e164 } : {}),
        recaptcha_token: recaptchaToken,
        polygon: geoJsonGeometry,
        utm: firstTouchUtmRef.current,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((data) => {
            if (data.code === 'recaptcha_missing' || data.code === 'recaptcha_invalid') {
              throw { code: data.code, message: data.message || 'Validá que no sos un robot.' };
            }
            throw new Error(data.message || `${data.error}`);
          });
        }
        return response.json();
      })
      .then((data) => {
        if (data.errors || data.error) {
          const errorCode = data.code || (data.errors && data.errors.code);
          if (errorCode === 'recaptcha_missing' || errorCode === 'recaptcha_invalid') {
            addToast('Validá que no sos un robot.', { appearance: 'warning', autoDismiss: true });
            recaptchaRef.current?.reset();
            setCaptchaValido(false);
            setRecaptchaToken('');
            return;
          }
          setErrorOpen(true);
          trackFunnelStep(FunnelSteps.FORM_SUBMIT_ERROR, { stage: 'api_response' });
          return;
        }

        clearFirstTouchUtm();
        firstTouchUtmRef.current = {};
        dispatch({ type: GIS, payload: null });
        trackFunnelStep(FunnelSteps.REPORT_SUCCESS);
        setSuccessOpen(true);
      })
      .catch((err) => {
        if (err.code === 'recaptcha_missing' || err.code === 'recaptcha_invalid') {
          addToast(err.message || 'Validá que no sos un robot.', { appearance: 'warning', autoDismiss: true });
          recaptchaRef.current?.reset();
          setCaptchaValido(false);
          setRecaptchaToken('');
          return;
        }
        setErrorOpen(true);
        trackFunnelStep(FunnelSteps.FORM_SUBMIT_ERROR, { stage: 'network' });
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    evaluate();
  };

  return (
    <form className="flex h-full flex-col p-4 md:p-6" onSubmit={handleSubmit}>
      <div className="mb-6">
        <p className="text-sm font-semibold text-brand-dark">Paso 2 — Tus datos</p>
        <p className="text-xs text-brand-earth">Te enviamos el reporte a tu email en minutos.</p>
      </div>

      {!hasPolygon && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          ↑ Primero marcá tu lote en el mapa para continuar
        </div>
      )}

      {hasPolygon && !hasValidField && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          ↑{' '}
          {fieldAreaError ||
            `El lote debe estar entre ${siteConfig.minHa} y ${siteConfig.freeHa} ha. Ajustá el polígono y guardá los cambios en el mapa.`}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label htmlFor="nombre" className="mb-1 block text-sm font-medium text-brand-dark">Nombre</label>
          <input
            id="nombre"
            name="given-name"
            autoComplete="given-name"
            className="w-full rounded-xl border border-brand-dark/10 bg-white px-4 py-3 text-sm outline-none transition focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20"
            value={nombre}
            type="text"
            onChange={(e) => setNombre(soloLetras(e.target.value))}
            onFocus={handleFieldFocus}
            placeholder="Tu nombre"
          />
        </div>
        <div>
          <label htmlFor="apellido" className="mb-1 block text-sm font-medium text-brand-dark">Apellido</label>
          <input
            id="apellido"
            name="family-name"
            autoComplete="family-name"
            className="w-full rounded-xl border border-brand-dark/10 bg-white px-4 py-3 text-sm outline-none transition focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20"
            value={apellido}
            type="text"
            onChange={(e) => setApellido(soloLetras(e.target.value))}
            onFocus={handleFieldFocus}
            placeholder="Tu apellido"
          />
        </div>
        <div>
          <label htmlFor="mail" className="mb-1 block text-sm font-medium text-brand-dark">Email</label>
          <input
            id="mail"
            name="email"
            autoComplete="email"
            className="w-full rounded-xl border border-brand-dark/10 bg-white px-4 py-3 text-sm outline-none transition focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20"
            value={mail}
            type="email"
            onChange={(e) => setMail(e.target.value)}
            onFocus={handleFieldFocus}
            placeholder="tu@email.com"
          />
        </div>
        <div>
          <label htmlFor="telefono" className="mb-1 flex items-baseline gap-2 text-sm font-medium text-brand-dark">
            <span>Teléfono</span>
            <span className="text-xs font-normal text-brand-earth">(opcional)</span>
          </label>
          <div className="relative">
            <span
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-brand-earth"
              aria-hidden
            >
              +54
            </span>
            <input
              id="telefono"
              name="telefono"
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              className={cn(
                'w-full rounded-xl border bg-white py-3 pl-14 pr-4 text-sm outline-none transition focus:ring-2',
                showPhoneError
                  ? 'border-red-400 focus:border-red-400 focus:ring-red-400/20'
                  : 'border-brand-dark/10 focus:border-brand-accent focus:ring-brand-accent/20',
              )}
              value={telefono}
              onChange={(e) => setTelefono(sanitizePhoneInput(e.target.value))}
              onFocus={handleFieldFocus}
              onBlur={() => {
                setPhoneTouched(true);
                if (telefono.trim()) {
                  setTelefono(formatPhoneDisplay(telefono));
                }
              }}
              placeholder="11 2345 6789"
              aria-invalid={showPhoneError}
              aria-describedby="telefono-hint telefono-error"
            />
          </div>
          <p id="telefono-hint" className="mt-1.5 text-xs text-brand-earth">
            Solo números de Argentina. Sin 0, 15 ni +54 - los agregamos nosotros.
          </p>
          {showPhoneError && (
            <p id="telefono-error" className="mt-1 text-xs font-medium text-red-600" role="alert">
              {phoneValidation.message}
            </p>
          )}
        </div>
      </div>

      <div className="mt-6 flex items-start gap-3 rounded-xl bg-surface p-4">
        <Mail className="mt-0.5 h-5 w-5 shrink-0 text-brand-accent" />
        <p className="text-xs leading-relaxed text-brand-earth">
          Tu análisis de productividad se genera automáticamente y llega a tu email en formato PDF en los próximos minutos.
        </p>
      </div>

      <div className="mt-6">
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LccJ90rAAAAAJoq_zh97vXM2XIUDcKm9bQ4IShg'}
          onChange={onCaptchaChange}
          onErrored={() => {
            // Load failure (domain, network, etc.) — no modal; submit still shows a toast if needed
            trackFunnelStep(FunnelSteps.FORM_SUBMIT_ERROR, { stage: 'recaptcha_load' });
          }}
        />
      </div>

      <button
        type="submit"
        className={cn(
          'mt-6 flex w-full items-center justify-center gap-2 rounded-xl py-4 text-base font-bold transition',
          hasValidField && !isSubmitting
            ? 'bg-brand-accent text-brand-dark hover:bg-brand-accent/90 shadow-md'
            : 'cursor-not-allowed bg-brand-dark/10 text-brand-earth',
        )}
        disabled={isSubmitting}
        data-cta="generate-analysis"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Generando tu reporte de ambientación…
          </>
        ) : (
          'Generar análisis gratis'
        )}
      </button>

      <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-brand-earth">
        <ShieldCheck className="h-3.5 w-3.5" />
        Tus datos están protegidos.
      </p>

      <SuccessBridgeModal isOpen={successOpen} onClose={handleSuccessClose} email={mail} />

      <Modal
        isOpen={errorOpen}
        onRequestClose={() => setErrorOpen(false)}
        style={errorModalStyles}
      >
        <h2 className="font-display text-xl font-bold text-brand-dark">Algo salió mal</h2>
        <p className="mt-3 text-sm text-brand-earth">
          No pudimos completar tu solicitud. Intentá nuevamente en unos minutos.
        </p>
        <p className="mt-2 text-sm">
          <a href="mailto:soporte@specific-ag.com" className="text-brand-accent hover:underline">
            soporte@specific-ag.com
          </a>
        </p>
        <button
          type="button"
          onClick={() => setErrorOpen(false)}
          className="mt-4 w-full rounded-xl bg-brand-dark py-3 text-sm font-semibold text-white"
        >
          Reintentar
        </button>
      </Modal>
    </form>
  );
};

export default Form;
